import React, { useEffect, useState } from "react";
import { Col, Row } from "rsuite";
import {
	ComposedChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
	Legend,
	Scatter,
	Bar,
} from "recharts";

interface ItemLevelData {
	[key: number]: any;
}
interface ExtendedRows {
	[key: number]: any[];
}
interface DivisionWiseGraphProps {
	itemLevelData: ItemLevelData;
	extendedRows: ExtendedRows;
	handleClick?: (e: any) => void;
	isFilterExpanded: boolean
}

interface itemLevelData {
	key: number;
	category: string;
	productType: string;
	InToleranceIndicator: number;
	isUserOverride: number;
	isRecOverride: number;
	itemDesc: string;
	division: string;
	week: string;
	itemId: string;
	itemBrand: string;
	divisionId: number;
	volume: number;
	revenue: number;
	rule: number;
	opportunity: number;
	margin: number;
	minThreshold: number;
	maxThreshold: number;
	cfpMin: number;
	cfpMax: number;
	leadingPrice: number;
	followingPrice: number;
	recOverride: number;
	customerId: string | null;
	newCustomerDesc: string;
	AvgPriceChange: number;
}
// Define the DataPoint interface
interface DataPoint {
	name: string;
	uv: number;
	pv: [number, number];
	amt: number;
	cnt: number;
	divisionId?: string;
	cfpMin?: number;
	cfpMax?: number;
	leadingPrice?: number;
	itemBrand?: string;
	followingPrice?: number;
	recOverride?: { isEditable: boolean; value: number };
}

// Define the DivisionWiseGraph component
const DivisionWiseGraph: React.FC<DivisionWiseGraphProps> = ({
	itemLevelData,
	extendedRows,
	handleClick,
	isFilterExpanded
}) => {
	const [tableHeight, setTableHeight] = useState<number>(150);
	const [graphWidth, setGraphWidth] = useState<number>(600);
	const [isGraphHeightUpdate, setIsGraphHeightUpdate] =
		useState<boolean>(true);
	const [data, setData] = useState<DataPoint[]>([]);

	useEffect(() => {
		const combinedList = Object.values(itemLevelData).flat();

		const dataPoints: DataPoint[] = combinedList.map((dummyData) => ({
			name: dummyData.division,
			uv: dummyData.volume,
			pv: [dummyData.cfpMin, dummyData.cfpMax],
			amt: dummyData.revenue,
			cnt: dummyData.opportunity,
			divisionId: dummyData.divisionId,
			cfpMin: dummyData.cfpMin,
			cfpMax: dummyData.cfpMax,
			leadingPrice: dummyData.leadingPrice,
			itemBrand: dummyData.itemBrand,
			followingPrice: dummyData.followingPrice,
			recOverride: dummyData.recOverride,
		}));
		setData(dataPoints);
	}, [itemLevelData]);

	useEffect(() => {
		const updateGraphWidth = () => {
			const id = document.getElementById("graph");
			if (id) {
				setGraphWidth(id.offsetWidth);
			}
		};

		const updateGraphHeight = () => {
			const id1 = document.getElementById("rowId");
			if (id1) {
				setTableHeight(id1.offsetHeight);
			}
		};

		updateGraphWidth();
		updateGraphHeight();

		window.addEventListener("resize", () => {
			updateGraphWidth();
			updateGraphHeight();
		});

		return () => {
			window.removeEventListener("resize", () => {
				updateGraphWidth();
				updateGraphHeight();
			});
		};
	}, []);

	useEffect(() => {
		if (data.length && isGraphHeightUpdate) {
			setIsGraphHeightUpdate(false);
			const id1 = document.getElementById("rowId");
			if (id1) {
				setTableHeight(id1.offsetHeight);
			}
		}
	}, [data, isGraphHeightUpdate]);

	const setDataStructureForGraph = (data: DataPoint[]) => {
		let barData: any = [];
		let div: any = [];

		// Process existing divisions first
		data.forEach((d) => {
			if (!div.includes(d.divisionId)) {
				div.push(d.divisionId);
				barData.push({
					name: d.divisionId,
					"Desired CFP Range": [
						Math.round(d.cfpMin! * 10) / 10,
						Math.round(d.cfpMax! * 10) / 10,
					],
					NBE: d.leadingPrice,
					ItemBrand: d.itemBrand,
					"Brand Price": d.followingPrice,
					"Recommended Price": d?.recOverride?.value,
				});
			}
		});

		// Remove any undefined entries
		return barData.filter((entry: any) => entry.name !== undefined);
	};

	const CustomTooltip = ({ active, payload, label }: any) => {
		if (active && payload && payload.length) {
			return (
				<div
					className="custom-tooltip bg-[#1a1916]"
				>
					<p className="label p-0 m-0 text-lightWhite text-[10px]">
						<label
						>
							Division :{" "}
						</label>
						<label
						>{`${label}`}</label>
					</p>
					{payload.map((d: any) => {
						if (d.name === "Desired CFP Range") {
							return (
								<p
									className="label p-0 m-0 text-lightWhite text-[10px]"
								>
									<label
									>
										{`${d.name} `}:{" "}
									</label>
									<label
									>
										{`$${d.value[0]}`} - {`$${d.value[1]}`}
									</label>
								</p>
							);
						} else if (d.name === "ItemBrand" || d.name === "NBE") {
							return (
								<p
									className="label p-0 m-0 text-lightWhite text-[10px]"
								>
									<label
									>
										{`${d.payload.ItemBrand} `}:{" "}
									</label>
									<label
									>
										{`$${d.payload["NBE"]}`}
									</label>
								</p>
							);
						} else {
							return (
								<p
									className="label p-0 m-0 text-lightWhite text-[10px]"
								>
									<label
									>
										{`${d.name} `}:{" "}
									</label>
									<label
									>
										{`$${d.value}`}
									</label>
								</p>
							);
						}
					})}
				</div>
			);
		}

		return null;
	};

	const barData = setDataStructureForGraph(data);

	return (
		<div id="rowId" className="h-full  flex justify-center items-center">
			{barData.length ? (
				<ComposedChart
					width={graphWidth - 20}
					height={isFilterExpanded ? tableHeight : tableHeight + 20}
					data={barData}
					margin={{
						top: 20,
						right: 20,
						bottom: 20,
						left: 20,
					}}
					style={{ marginLeft: "2%" }}
				>
					<CartesianGrid
						stroke="#000"
						strokeDasharray="none"
						horizontal={false}
					/>
					<XAxis
						fontSize={"0.8vw"}
						label={{
							style: {
								textAnchor: "middle",
								color: "black",
								marginLeft: "1%",
							},
							value: "Click on the graph values for historical trends",
							position: "insideRight",
							offset: 140,
							fill: "black",
							fontSize: "0.8vw"

						}}
						tick={{ display: "none" }}
						stroke="#000"
						tickLine={false}
						dataKey="name"
					/>
					<YAxis
						stroke="#000"
						fontSize={"0.8vw"}
						tickFormatter={(value) => "$" + value}
						style={{ color: "#000", fontSize: "0.8vw" }}
						tickCount={5}
						type="number"
						domain={["dataMin - 3", "auto"]}
						interval="preserveStartEnd"
						label={{
							style: {
								textAnchor: "middle",
								color: "black",
							},
							value: "Cost For Price ($/Case)",
							angle: -90,
							position: "insideLeft",
							fill: "black",
							fontSize: "0.8vw"

						}}
					/>
					<Tooltip
						cursor={{ fill: "transparent" }}
						contentStyle={{
							color: "white",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						content={<CustomTooltip />}
					/>
					<Legend
						className="chart-legend"
						align="right"
						verticalAlign="top"
						iconSize={14}
						height={30}
						wrapperStyle={{
							fontSize: "0.8vw",
						}}
					/>
					<Bar
						legendType="rect"
						dataKey="Desired CFP Range"
						barSize={20}
						fill="#8d8d8d"
						onClick={handleClick}
					/>
					<Scatter
						onClick={handleClick}
						legendType="diamond"
						shape="diamond"
						dataKey="NBE"
						fill="#e89c38"
					/>
					<Scatter
						onClick={handleClick}
						legendType="square"
						shape="square"
						dataKey="Brand Price"
						fill="#db4c42"
					/>
					<Scatter
						onClick={handleClick}
						legendType="cross"
						shape="cross"
						dataKey="Recommended Price"
						fill="yellow"
					/>
				</ComposedChart>
			) : (
				<span className="italic font-normal text-[2.5vh] text-center text-black">
					Click on the left Table to get the Detailed view
				</span>
			)}
		</div>
	);
};

export default DivisionWiseGraph;
